import { useState, useEffect, useCallback, useMemo } from 'react';
import { createClient } from '@supabase/supabase-js';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Avatar,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import { Compare as CompareIcon } from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar
} from 'recharts';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

const toDateKey = (value) => (value ? value.split('T')[0] : null);
const parseDateKey = (dateKey) => new Date(`${dateKey}T00:00:00`);
const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};
const formatShortDate = (date) =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const Comparisons = () => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!supabase) {
        throw new Error('Supabase client not configured.');
      }

      const pageSize = 1000;
      let from = 0;
      let allData = [];
      let hasMore = true;

      while (hasMore) {
        const { data, error: fetchError } = await supabase
          .from('detection')
          .select(`
            absolute_start_time,
            confidence,
            species (
              common_name
            )
          `)
          .gte('confidence', 0.1)
          .order('absolute_start_time')
          .range(from, from + pageSize - 1);

        if (fetchError) throw fetchError;

        allData = allData.concat(data || []);
        hasMore = data && data.length === pageSize;
        from += pageSize;
      }

      setRawData(allData);
    } catch (err) {
      console.error('Error fetching comparison data:', err);
      setError(err.message);
      setRawData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const {
    periodComparisonData,
    topSpeciesComparisonData,
    hourlyComparisonData,
    cumulativeTrendData,
    yearlyComparisonData,
    windowLabels
  } = useMemo(() => {
    if (rawData.length === 0) {
      return {
        periodComparisonData: [],
        topSpeciesComparisonData: [],
        hourlyComparisonData: [],
        cumulativeTrendData: [],
        yearlyComparisonData: [],
        windowLabels: { current: 'Current', previous: 'Previous' }
      };
    }

    const dateKeys = rawData
      .map((item) => toDateKey(item.absolute_start_time))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));

    const latestKey = dateKeys[dateKeys.length - 1];
    const latestDate = parseDateKey(latestKey);
    const currentEnd = latestDate;
    const currentStart = addDays(currentEnd, -13);
    const previousEnd = addDays(currentStart, -1);
    const previousStart = addDays(previousEnd, -13);

    const windowLabelsLocal = {
      current: `${formatShortDate(currentStart)}–${formatShortDate(currentEnd)}`,
      previous: `${formatShortDate(previousStart)}–${formatShortDate(previousEnd)}`
    };

    const currentCounts = Array(14).fill(0);
    const previousCounts = Array(14).fill(0);
    const currentSpeciesCounts = {};
    const previousSpeciesCounts = {};
    const currentHourlyWeekday = Array(24).fill(0);
    const currentHourlyWeekend = Array(24).fill(0);
    const currentWeekdayDates = new Set();
    const currentWeekendDates = new Set();
    const dailyTotals = {};
    const yearlyTotals = {};

    rawData.forEach((detection) => {
      const dateKey = toDateKey(detection.absolute_start_time);
      if (!dateKey) return;
      const dateObj = parseDateKey(dateKey);
      const hour = new Date(detection.absolute_start_time).getHours();
      const species = detection.species?.common_name;

      if (dateObj >= currentStart && dateObj <= currentEnd) {
        const idx = Math.floor((dateObj - currentStart) / (24 * 60 * 60 * 1000));
        if (idx >= 0 && idx < 14) {
          currentCounts[idx] += 1;
        }
        if (species) {
          currentSpeciesCounts[species] = (currentSpeciesCounts[species] || 0) + 1;
        }
        const weekday = dateObj.getDay();
        if (weekday === 0 || weekday === 6) {
          currentHourlyWeekend[hour] += 1;
          currentWeekendDates.add(dateKey);
        } else {
          currentHourlyWeekday[hour] += 1;
          currentWeekdayDates.add(dateKey);
        }
      } else if (dateObj >= previousStart && dateObj <= previousEnd) {
        const idx = Math.floor((dateObj - previousStart) / (24 * 60 * 60 * 1000));
        if (idx >= 0 && idx < 14) {
          previousCounts[idx] += 1;
        }
        if (species) {
          previousSpeciesCounts[species] = (previousSpeciesCounts[species] || 0) + 1;
        }
      }

      dailyTotals[dateKey] = (dailyTotals[dateKey] || 0) + 1;

      const yearKey = dateObj.getFullYear();
      yearlyTotals[yearKey] = (yearlyTotals[yearKey] || 0) + 1;
    });

    const periodData = Array.from({ length: 14 }, (_, idx) => ({
      day: `Day ${idx + 1}`,
      current: currentCounts[idx],
      previous: previousCounts[idx]
    }));

    const topSpecies = Array.from(
      new Set([
        ...Object.keys(currentSpeciesCounts),
        ...Object.keys(previousSpeciesCounts)
      ])
    )
      .map((name) => ({
        name,
        current: currentSpeciesCounts[name] || 0,
        previous: previousSpeciesCounts[name] || 0
      }))
      .sort((a, b) => (b.current + b.previous) - (a.current + a.previous))
      .slice(0, 6);

    const weekdayDays = currentWeekdayDates.size || 1;
    const weekendDays = currentWeekendDates.size || 1;
    const hourlyData = Array.from({ length: 24 }, (_, hour) => ({
      hour,
      label: hour === 0 ? '12a' : hour < 12 ? `${hour}a` : hour === 12 ? '12p' : `${hour - 12}p`,
      weekdayAvg: Number((currentHourlyWeekday[hour] / weekdayDays).toFixed(2)),
      weekendAvg: Number((currentHourlyWeekend[hour] / weekendDays).toFixed(2))
    }));

    const dailyKeys = Object.keys(dailyTotals).sort((a, b) => a.localeCompare(b));
    const recentDailyKeys = dailyKeys.slice(-90);
    let cumulative = 0;
    const cumulativeData = recentDailyKeys.map((dateKey) => {
      cumulative += dailyTotals[dateKey];
      const dateObj = parseDateKey(dateKey);
      return {
        date: dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        cumulative
      };
    });

    const yearlyData = Object.entries(yearlyTotals)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([year, detections]) => ({
        year,
        detections
      }));

    return {
      periodComparisonData: periodData,
      topSpeciesComparisonData: topSpecies,
      hourlyComparisonData: hourlyData,
      cumulativeTrendData: cumulativeData,
      yearlyComparisonData: yearlyData,
      windowLabels: windowLabelsLocal
    };
  }, [rawData]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'info.main' }}>
            <CompareIcon />
          </Avatar>
          <Typography variant="h4" fontWeight="bold">
            ⚖️ Comparisons
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Comparative analysis between different time periods, locations, and species populations.
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress />
            <Typography variant="h6">Loading comparison data...</Typography>
          </Stack>
        </Box>
      ) : error ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Alert severity="error">Error loading comparisons: {error}</Alert>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3
          }}
        >
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 Period Comparison
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Daily detections: {windowLabels.current} vs {windowLabels.previous}.
              </Typography>
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={periodComparisonData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="current" stroke="#1976d2" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="previous" stroke="#ff8f00" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🦅 Species vs Species
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Top species counts in each window.
              </Typography>
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topSpeciesComparisonData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-25} textAnchor="end" height={50} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="current" fill="#1976d2" barSize={18} />
                    <Bar dataKey="previous" fill="#ff8f00" barSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📈 Trend Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Cumulative detections (last 90 days).
              </Typography>
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={cumulativeTrendData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="cumulative" stroke="#6a1b9a" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🗓️ Yearly Comparison
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total detections by year.
              </Typography>
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={yearlyComparisonData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="detections" fill="#00897b" barSize={32} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default Comparisons;
